/**
 * useHashNavigation - Reusable hook for URL hash-based navigation with scroll
 *
 * Handles:
 * - URL hash changes (browser back/forward navigation)
 * - Scrolling to elements with ResizeObserver for layout shift handling
 * - Hash redirects for backwards compatibility
 * - Integration with tab/section selection
 *
 * @example
 * useHashNavigation({
 *   isReady: router.isReady && !isLoading && !!data,
 *   asPath: router.asPath,
 *   tabOptions: eventTabOptions,
 *   setSelectedTab,
 *   hashRedirects: { description: "additional-info" },
 *   onRedirect: (hash) => router.replace({ hash }, undefined, { shallow: true }),
 * });
 */
import { useCallback, useEffect, useRef } from "react";
import { waitForElementToLoad } from "@shared/services/loaders/element-loader";

interface TabOption {
  id: number;
  hash: string | null;
}

interface UseHashNavigationOptions {
  /**
   * Whether the hook is ready to process hash changes.
   * Should be true when router is ready and data is loaded.
   */
  isReady: boolean;

  /**
   * The current URL path including hash (e.g., router.asPath)
   */
  asPath: string | undefined;

  /**
   * Array of tab options with id and hash
   */
  tabOptions: TabOption[];

  /**
   * Callback to set the selected tab
   * Can accept either a number or a functional update
   */
  setSelectedTab: (tabId: number | ((prev: number) => number)) => void;

  /**
   * Optional hash redirects mapping (e.g., { description: "additional-info" })
   */
  hashRedirects?: Record<string, string>;

  /**
   * Callback to perform hash redirect (e.g., router.replace)
   */
  onRedirect?: (newHash: string) => void;

  /**
   * Optional ref to skip scrolling (e.g., when tab was clicked manually)
   */
  skipScrollRef?: React.RefObject<boolean>;

  /**
   * Optional callback to disable scroll sync during navigation
   */
  onScrollSyncChange?: (disabled: boolean) => void;

  /**
   * Optional ref to track manual tab changes
   * If current value is not null, hash navigation will be skipped
   */
  manualTabChangeRef?: React.RefObject<number | null>;
}

interface UseHashNavigationReturn {
  /**
   * Call this to mark a hash as processed (e.g., when manually changing tabs)
   * Prevents the hook from re-processing that hash
   */
  markHashProcessed: (hash: string) => void;
}

export function useHashNavigation({
  isReady,
  asPath,
  tabOptions,
  setSelectedTab,
  hashRedirects,
  onRedirect,
  skipScrollRef,
  onScrollSyncChange,
  manualTabChangeRef,
}: UseHashNavigationOptions): UseHashNavigationReturn {
  const lastProcessedHashRef = useRef<string | null>(null);

  const markHashProcessed = useCallback((hash: string) => {
    lastProcessedHashRef.current = hash;
  }, []);

  useEffect(() => {
    // Wait until ready before handling hash navigation
    if (!isReady) return;

    const elementID = asPath?.split("#")[1];

    // Skip if no hash or we already processed this hash
    if (!elementID || elementID === lastProcessedHashRef.current) return;

    // Handle hash redirects (e.g., #description -> #additional-info)
    if (hashRedirects && hashRedirects[elementID] && onRedirect) {
      onRedirect(hashRedirects[elementID]);
      return;
    }

    // Skip if manual tab change is in progress
    if (
      manualTabChangeRef?.current !== null &&
      manualTabChangeRef?.current !== undefined
    ) {
      return;
    }

    // Skip processing entirely if skipScrollRef is true (manual tab click in progress)
    // Re-enable scroll sync after 500ms to match legacy behaviour when scroll was skipped
    if (skipScrollRef?.current) {
      const timeoutId = window.setTimeout(() => {
        onScrollSyncChange?.(false);
      }, 500);
      // eslint-disable-next-line consistent-return -- React useEffect: cleanup only when we scheduled work
      return () => clearTimeout(timeoutId);
    }

    const validHash = tabOptions?.find((tab) => tab.hash === elementID);

    if (validHash) {
      // Disable scroll-based tab sync during URL navigation
      onScrollSyncChange?.(true);

      // Update selected tab
      setSelectedTab((currentTab: number) => {
        if (currentTab === validHash.id) {
          return currentTab; // No change needed
        }
        return validHash.id;
      });

      const pendingRef = {
        rafId: undefined as number | undefined,
        timeoutId: undefined as ReturnType<typeof setTimeout> | undefined,
        observer: undefined as ResizeObserver | undefined,
      };

      pendingRef.rafId = requestAnimationFrame(() => {
        waitForElementToLoad(`#${elementID}`).then(() => {
          const element = document.getElementById(elementID);
          if (!element) return;

          // Mark hash as processed only after the element is found.
          // This allows retries if the effect re-runs before the element
          // appears in the DOM (e.g., when gated by EventAuthorization).
          lastProcessedHashRef.current = elementID;

          // Initial scroll
          element.scrollIntoView({ behavior: "smooth" });

          // Watch for layout shifts caused by dynamic content loading
          // and re-scroll to keep the section in view
          let lastTop = element.getBoundingClientRect().top;
          pendingRef.observer = new ResizeObserver(() => {
            const currentTop = element.getBoundingClientRect().top;
            // Only re-scroll if the element has shifted significantly (more than 50px)
            if (Math.abs(currentTop - lastTop) > 50) {
              element.scrollIntoView({ behavior: "smooth" });
              lastTop = element.getBoundingClientRect().top;
            }
          });

          // Observe the parent container for size changes
          const contentContainer =
            document.querySelector('[data-scroll-container="true"]') ||
            element.parentElement;

          if (contentContainer && pendingRef.observer) {
            pendingRef.observer.observe(contentContainer);
          }

          // Stop observing after 3 seconds and re-enable scroll sync
          pendingRef.timeoutId = setTimeout(() => {
            pendingRef.observer?.disconnect();
            onScrollSyncChange?.(false);
          }, 3000);
        });
      });

      // eslint-disable-next-line consistent-return -- React useEffect: cleanup only when we scheduled work
      return () => {
        if (pendingRef.rafId !== undefined)
          cancelAnimationFrame(pendingRef.rafId);
        if (pendingRef.timeoutId !== undefined)
          clearTimeout(pendingRef.timeoutId);
        pendingRef.observer?.disconnect();
      };
    }
  }, [
    isReady,
    asPath,
    tabOptions,
    setSelectedTab,
    hashRedirects,
    onRedirect,
    skipScrollRef,
    onScrollSyncChange,
    manualTabChangeRef,
  ]);

  return { markHashProcessed };
}
